import { Suspense, lazy, useState, useLayoutEffect } from 'react';
import { Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { RoutePath, GuardedRoute, history } from 'src/router';

import store, { useSelector } from 'src/store';
import { authRoleSelector, isLoggedInSelector } from 'src/store/selectors/authSelector';

import { PageLoading } from '@itm/shared-frontend/lib/components';

import AuthProvider from 'src/hocs/AuthProvider';
import SyncFiltersWithURL from 'src/hocs/SyncFiltersWithURL';
import Layout from 'src/components/layout/Layout';
import BreadcrumbsTrail from 'src/components/BreadcrumbsTrail';

import usePreviousLocationInStore from 'src/hooks/usePreviousLocationInStore';

import LoginRedirectPage from 'src/pages/loginRedirect';
import PermissionDeniedPage from 'src/pages/permissionDenied';

import { Role } from 'src/types';

const ProductInfoPage = lazy(() => import(/* webpackChunkName: "info" */ 'src/pages/productInfo/ProductInfo'));
const ConfigurationRoutes = lazy(() => import(/* webpackChunkName: "configuration" */ 'src/pages/configuration'));
const DataManagementRoutes = lazy(() => import(/* webpackChunkName: "dataManagement" */ 'src/pages/dataManagement'));
const ReportingRoutes = lazy(() => import(/* webpackChunkName: "reporting" */ 'src/pages/reporting'));

function RootRoutesFallback() {
  return (
    <div className="px-3">
      <div className="container">
        <PageLoading />
      </div>
    </div>
  );
}

function RootRoutes() {
  usePreviousLocationInStore();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userRole = useSelector(authRoleSelector);

  const redirectRoute = isLoggedIn
    ? userRole === Role.SuperAdmin ||
      userRole === Role.Support ||
      userRole === Role.Analyst ||
      userRole === Role.ClientAdmin
      ? RoutePath.configurationRoot
      : RoutePath.dataManagementProcessSummary
    : RoutePath.loginRedirect;

  return (
    <Layout>
      <Suspense fallback={<RootRoutesFallback />}>
        <BreadcrumbsTrail />

        <Routes>
          <Route index element={<Navigate to={redirectRoute} replace={true} />} />

          <Route path={RoutePath.loginRedirect} element={<LoginRedirectPage />} />
          <Route path={RoutePath.permissionDenied} element={<PermissionDeniedPage />} />

          <Route
            path={`${RoutePath.configurationRoot}/*`}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: {
                    allowList: [Role.SuperAdmin, Role.Support, Role.Analyst, Role.ClientAdmin, Role.EmployerAdmin],
                  },
                }}
              >
                <ConfigurationRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={`${RoutePath.dataManagementRoot}/*`}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: {
                    allowList: [
                      Role.SuperAdmin,
                      Role.Support,
                      Role.Analyst,
                      Role.ClientAdmin,
                      Role.Manager,
                      Role.EmployerAdmin,
                    ],
                  },
                }}
              >
                <DataManagementRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={`${RoutePath.reportingRoot}/*`}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: {
                    allowList: [
                      Role.SuperAdmin,
                      Role.Support,
                      Role.Analyst,
                      Role.ClientAdmin,
                      Role.Manager,
                      Role.EmployerAdmin,
                    ],
                  },
                }}
              >
                <ReportingRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={RoutePath.productInfo}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: { allowList: [Role.SuperAdmin, Role.Support] },
                }}
              >
                <ProductInfoPage />
              </GuardedRoute>
            }
          />

          <Route path="*" element={<Navigate to={RoutePath.root} />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function RootRouter() {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });
  useLayoutEffect(() => history.listen(setState), []);
  return (
    <Router location={state.location} navigationType={state.action} navigator={history}>
      <Provider store={store}>
        <AuthProvider>
          <SyncFiltersWithURL>
            <RootRoutes />
          </SyncFiltersWithURL>
        </AuthProvider>
      </Provider>
    </Router>
  );
}

export default RootRouter;
